import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { socketManager } from 'utils/socketManager';
import {
  socketConnecting,
  socketConnected,
  socketDisconnected,
  socketError,
  socketReconnectAttempt,
} from 'store/slices/activitySlice';
import { Activity } from 'types/interfaces/activity';
import { prependActivityToCache } from 'store/services/activity/activityApi';

interface UseActivitySocketOptions {
  enabled?: boolean;
  onActivity?: (activity: Activity) => void;
}

interface UseActivitySocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
}

/**
 * Hook to manage Socket.IO connection for activity feed.
 * Uses shared socket manager singleton.
 */
export const useActivitySocket = (
  options: UseActivitySocketOptions = {}
): UseActivitySocketReturn => {
  const { enabled = true, onActivity } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const { isConnected, isConnecting, connectionError } = useSelector(
    (state: RootState) => state.activity
  );

  // Stable callback ref to avoid effect re-runs
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  // Handle incoming activity events
  const handleActivityEvent = useCallback(
    (data: { activity: Activity }) => {
      if (data?.activity) {
        // Update RTK Query cache with new activity
        prependActivityToCache(dispatch, data.activity);

        // Call optional callback
        if (onActivityRef.current) {
          onActivityRef.current(data.activity);
        }
      }
    },
    [dispatch]
  );

  // Connection state handler
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        dispatch(socketConnected());
      } else {
        dispatch(socketDisconnected());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(socketConnecting());
    hasAcquiredRef.current = true;

    const socket = socketManager.acquireSocket();

    // Subscribe to connection state changes
    socketManager.addConnectionListener(handleConnectionChange);

    // Check current connection state
    if (socket.connected) {
      dispatch(socketConnected());
    }

    // Subscribe to socket events directly for immediate state updates
    const onConnect = () => {
      console.log('[ActivitySocket] Connected');
      dispatch(socketConnected());
    };

    const onDisconnect = (reason: string) => {
      console.log('[ActivitySocket] Disconnected:', reason);
      dispatch(socketDisconnected());
    };

    const onConnectError = (error: Error) => {
      console.error('[ActivitySocket] Connection error:', error.message);
      dispatch(socketError(error.message));
    };

    const onReconnectAttempt = (attemptNumber: number) => {
      console.log('[ActivitySocket] Reconnect attempt:', attemptNumber);
      dispatch(socketReconnectAttempt());
    };

    const onReconnectFailed = () => {
      console.error('[ActivitySocket] Reconnection failed after max attempts');
      dispatch(socketError('Reconnection failed'));
    };

    // Register connection event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);

    // Activity event handlers
    socket.on('activity:trade_sent', handleActivityEvent);
    socket.on('activity:trade_received', handleActivityEvent);
    socket.on('activity:want_added', handleActivityEvent);

    // Cleanup on unmount
    return () => {
      console.log('[ActivitySocket] Cleaning up');
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('activity:trade_sent', handleActivityEvent);
      socket.off('activity:trade_received', handleActivityEvent);
      socket.off('activity:want_added', handleActivityEvent);
      socketManager.removeConnectionListener(handleConnectionChange);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, dispatch, handleActivityEvent, handleConnectionChange]);

  return {
    isConnected,
    isConnecting,
    connectionError,
  };
};

export default useActivitySocket;
